<template>
  <div class="applications-table-container">
    <div v-if="loading" class="text-center py-8">
      <h2>Loading applications...</h2>
    </div>

    <div
      v-else-if="!applications || applications.length === 0"
      class="text-center py-8"
    >
      <h2>No applications found for this product</h2>
      <p class="text-gray-500 mt-2">Click "Add Applications" to get started</p>
    </div>

    <div v-else>
      <!-- Bulk Actions Bar -->
      <div
        class="bulk-actions-bar"
        v-if="selectedIds.length > 0 && userCanEditProducts"
      >
        <div class="selection-info">
          {{ selectedIds.length }} application(s) selected
        </div>
        <div class="action-buttons">
          <button @click="showBulkNotesModal = true" class="btn-info-button">
            Add Notes to Selected
          </button>
          <button @click="showBulkQtyModal = true" class="btn-info-button">
            Update Quantity
          </button>
          <button @click="deleteSelected" class="btn-error-button">
            Delete Selected
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-section">
        <div class="items-per-page">
          <label>Items per page:</label>
          <select v-model="itemsPerPage" class="regular bordered">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <button @click="clearFilters" class="btn-clear-button">
          Clear Filters
        </button>
      </div>

      <!-- Applications Table -->
      <div class="table-wrapper">
        <table class="applications-table">
          <thead>
            <tr>
              <th width="50" v-if="userCanEditProducts">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  @change="toggleSelectAll"
                />
              </th>
              <th @click="sortBy('catalogCode')">
                Code {{ sortArrow("catalogCode") }}
              </th>
              <th @click="sortBy('make')">Make {{ sortArrow("make") }}</th>
              <th @click="sortBy('model')">Model {{ sortArrow("model") }}</th>
              <th @click="sortBy('year')">Year {{ sortArrow("year") }}</th>
              <th @click="sortBy('engineName')">
                Engine {{ sortArrow("engineName") }}
              </th>
              <th @click="sortBy('region')">
                Region {{ sortArrow("region") }}
              </th>
              <th @click="sortBy('qty_required')">
                Qty Required {{ sortArrow("qty_required") }}
              </th>
              <th width="100" v-if="userCanEditProducts">Actions</th>
            </tr>
            <!-- Filter Row -->
            <tr class="filter-row">
              <th v-if="userCanEditProducts"></th>
              <th>
                <input
                  v-model="filters.catalogCode"
                  type="text"
                  placeholder="Filter code..."
                  class="filter-input"
                />
              </th>
              <th>
                <input
                  v-model="filters.make"
                  type="text"
                  placeholder="Filter make..."
                  class="filter-input"
                />
              </th>
              <th>
                <input
                  v-model="filters.model"
                  type="text"
                  placeholder="Filter model..."
                  class="filter-input"
                />
              </th>
              <th>
                <input
                  v-model="filters.year"
                  type="text"
                  placeholder="Filter year..."
                  class="filter-input"
                />
              </th>
              <th>
                <input
                  v-model="filters.engine"
                  type="text"
                  placeholder="Filter engine..."
                  class="filter-input"
                />
              </th>
              <th>
                <input
                  v-model="filters.region"
                  type="text"
                  placeholder="Filter region..."
                  class="filter-input"
                />
              </th>
              <th></th>
              <th v-if="userCanEditProducts"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="app in paginatedApplications"
              :key="app.id"
              :class="{ selected: selectedIds.includes(app.id) }"
              @click="toggleSelect(app.id)"
            >
              <td @click.stop v-if="userCanEditProducts">
                <input
                  type="checkbox"
                  :checked="selectedIds.includes(app.id)"
                  @change="toggleSelect(app.id)"
                />
              </td>
              <td>
                <div v-if="app.catalogCode" class="catalog-info">
                  <code class="catalog-code">{{ app.catalogCode }}</code>
                  <span v-if="app.vehicleFamily" class="family-name">{{
                    app.vehicleFamily
                  }}</span>
                </div>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td>{{ app.make }}</td>
              <td class="font-bold">{{ app.model }}</td>
              <td class="font-bold">{{ app.year }}</td>
              <td>
                {{ app.engineName }} {{ app.engineLiter }} {{ app.aspiration }}
              </td>
              <td>{{ app.region || "—" }}</td>
              <td>{{ app.qty_required || "—" }}</td>
              <td class="actions-cell" v-if="userCanEditProducts">
                <button
                  @click.stop="editNotes(app)"
                  class="action-btn"
                  title="Edit notes"
                >
                  📝
                </button>
                <button
                  @click.stop="deleteApplication(app.id)"
                  class="action-btn delete"
                  title="Delete"
                >
                  🗑️
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-section">
        <div class="pagination-info">
          Showing {{ startIndex + 1 }} to {{ endIndex }} of
          {{ filteredApplications.length }} applications
        </div>
        <div class="pagination-controls">
          <button
            @click="currentPage--"
            :disabled="currentPage === 1"
            class="btn-dark-button"
          >
            Previous
          </button>
          <span class="page-info"
            >Page {{ currentPage }} of {{ totalPages }}</span
          >
          <button
            @click="currentPage++"
            :disabled="currentPage === totalPages"
            class="btn-dark-button"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Notes Modal -->
    <NotesModal
      v-if="showNotesModal"
      :application="editingApplication"
      @close="showNotesModal = false"
      @save="saveNotes"
    />

    <!-- Bulk Notes Modal -->
    <BulkNotesModal
      v-if="showBulkNotesModal"
      :count="selectedIds.length"
      @close="showBulkNotesModal = false"
      @save="saveBulkNotes"
    />

    <!-- Bulk Quantity Modal -->
    <BulkQuantityModal
      v-if="showBulkQtyModal"
      :count="selectedIds.length"
      @close="showBulkQtyModal = false"
      @save="saveBulkQuantity"
    />
  </div>
</template>

<script>
import {
  saveApplicationNotes,
  saveBulkNotes,
  updateBulkQuantity,
  deleteApplications
} from "@/api/store/ApplicationsService";
import NotesModal from "./NotesModal.vue";
import BulkNotesModal from "./BulkNotesModal.vue";
import BulkQuantityModal from "./BulkQuantityModal.vue";

export default {
  name: "ApplicationsTable",
  components: {
    NotesModal,
    BulkNotesModal,
    BulkQuantityModal
  },
  props: {
    unified: {
      type: String,
      required: true
    },
    applications: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedIds: [],
      filters: {
        catalogCode: "",
        make: "",
        model: "",
        year: "",
        engine: "",
        region: ""
      },
      sortField: null,
      sortDirection: "asc",
      currentPage: 1,
      itemsPerPage: 25,
      showNotesModal: false,
      showBulkNotesModal: false,
      showBulkQtyModal: false,
      editingApplication: null
    };
  },
  computed: {
    userCanEditProducts() {
      return this.$store.getters["auth/userHasPermission"]("can_edit_products");
    },
    filteredApplications() {
      let filtered = [...this.applications];

      // Apply filters
      if (this.filters.catalogCode) {
        filtered = filtered.filter(app =>
          (app.catalogCode || "")
            .toLowerCase()
            .includes(this.filters.catalogCode.toLowerCase())
        );
      }
      if (this.filters.make) {
        filtered = filtered.filter(app =>
          app.make.toLowerCase().includes(this.filters.make.toLowerCase())
        );
      }
      if (this.filters.model) {
        filtered = filtered.filter(app =>
          app.model.toLowerCase().includes(this.filters.model.toLowerCase())
        );
      }
      if (this.filters.year) {
        filtered = filtered.filter(app =>
          app.year.toString().includes(this.filters.year)
        );
      }
      if (this.filters.engine) {
        filtered = filtered.filter(app =>
          app.engineName
            .toLowerCase()
            .includes(this.filters.engine.toLowerCase())
        );
      }
      if (this.filters.region) {
        filtered = filtered.filter(app =>
          (app.region || "")
            .toLowerCase()
            .includes(this.filters.region.toLowerCase())
        );
      }

      // Apply sorting
      if (this.sortField) {
        filtered.sort((a, b) => {
          const aVal = a[this.sortField];
          const bVal = b[this.sortField];
          const multiplier = this.sortDirection === "asc" ? 1 : -1;

          if (aVal < bVal) return -1 * multiplier;
          if (aVal > bVal) return 1 * multiplier;
          return 0;
        });
      }

      return filtered;
    },
    paginatedApplications() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredApplications.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredApplications.length / this.itemsPerPage);
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(
        this.startIndex + this.itemsPerPage,
        this.filteredApplications.length
      );
    },
    allSelected() {
      return (
        this.paginatedApplications.length > 0 &&
        this.paginatedApplications.every(app =>
          this.selectedIds.includes(app.id)
        )
      );
    }
  },
  watch: {
    currentPage() {
      this.selectedIds = [];
    },
    itemsPerPage() {
      this.currentPage = 1;
    }
  },
  methods: {
    toggleSelect(id) {
      const index = this.selectedIds.indexOf(id);
      if (index === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(index, 1);
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.paginatedApplications.forEach(app => {
          const index = this.selectedIds.indexOf(app.id);
          if (index !== -1) {
            this.selectedIds.splice(index, 1);
          }
        });
      } else {
        this.paginatedApplications.forEach(app => {
          if (!this.selectedIds.includes(app.id)) {
            this.selectedIds.push(app.id);
          }
        });
      }
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
    },
    sortArrow(field) {
      if (this.sortField !== field) return "";
      return this.sortDirection === "asc" ? "↑" : "↓";
    },
    clearFilters() {
      this.filters = {
        catalogCode: "",
        make: "",
        model: "",
        year: "",
        engine: "",
        region: ""
      };
    },
    editNotes(application) {
      this.editingApplication = { ...application };
      this.showNotesModal = true;
    },
    async saveNotes(notes) {
      try {
        await saveApplicationNotes(this.editingApplication.id, notes);
        this.$toast.success("Notes saved");
        this.showNotesModal = false;
        this.$emit("refresh");
      } catch (error) {
        this.$toast.error("Error saving notes");
      }
    },
    async saveBulkNotes(notes) {
      try {
        await saveBulkNotes(this.selectedIds, notes);
        this.$toast.success("Bulk notes saved");
        this.showBulkNotesModal = false;
        this.selectedIds = [];
        this.$emit("refresh");
      } catch (error) {
        this.$toast.error("Error saving bulk notes");
      }
    },
    async saveBulkQuantity(quantity) {
      try {
        await updateBulkQuantity(this.selectedIds, quantity);
        this.$toast.success("Quantities updated");
        this.showBulkQtyModal = false;
        this.selectedIds = [];
        this.$emit("refresh");
      } catch (error) {
        this.$toast.error("Error updating quantities");
      }
    },
    async deleteApplication(id) {
      if (!confirm("Delete this application?")) return;

      try {
        await deleteApplications(this.unified, id);
        this.$toast.success("Application deleted");
        this.$emit("refresh");
      } catch (error) {
        this.$toast.error("Error deleting application");
      }
    },
    async deleteSelected() {
      if (!confirm(`Delete ${this.selectedIds.length} applications?`)) return;

      try {
        await deleteApplications(this.unified, this.selectedIds);
        this.$toast.success("Applications deleted");
        this.selectedIds = [];
        this.$emit("refresh");
      } catch (error) {
        this.$toast.error("Error deleting applications");
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.applications-table-container {
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
}

.bulk-actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #eff6ff;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;

  .selection-info {
    font-weight: 600;
    color: #1e40af;
  }

  .action-buttons {
    display: flex;
    gap: 0.5rem;
  }
}

.filters-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  .items-per-page {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    label {
      font-weight: 500;
    }

    select {
      width: 80px;
    }
  }
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.applications-table {
  width: 100%;
  font-size: 1.3rem;
  border-collapse: collapse;

  thead {
    background: #111827;
    color: white;

    th {
      padding: 0.75rem;
      text-align: left;
      font-weight: 600;
      cursor: pointer;
      user-select: none;

      &:hover {
        background: #1f2937;
      }
    }

    .filter-row {
      background: #374151;

      th {
        padding: 0.5rem;
        cursor: default;

        &:hover {
          background: #374151;
        }
      }
    }
  }

  .filter-input {
    width: 100%;
    padding: 0.25rem 0.5rem;
    border: 1px solid #4b5563;
    border-radius: 0.25rem;
    background: #1f2937;
    color: white;

    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
      border-color: #60a5fa;
    }
  }

  tbody {
    tr {
      cursor: pointer;
      transition: background 0.15s;

      &:nth-child(even) {
        background: #f9fafb;
      }

      &:hover {
        background: #fef3c7;
      }

      &.selected {
        background: #dcfce7;
      }

      td {
        padding: 0.75rem;
        border-bottom: 1px solid #e5e7eb;
      }

      .catalog-code {
        background: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        font-family: monospace;
        font-size: 0.8125rem;
        color: #374151;
        font-weight: 600;
      }
    }
  }

  .actions-cell {
    display: flex;
    gap: 0.5rem;
  }

  .action-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.25rem;
    padding: 0.25rem;
    transition: transform 0.15s;

    &:hover {
      transform: scale(1.2);
    }

    &.delete:hover {
      filter: brightness(0.8);
    }
  }
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 1rem;

    .page-info {
      font-weight: 500;
    }

    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
